import { css, StyleSheet } from 'aphrodite';
import type { Options } from 'react-markdown';
import { borderColor, brandPrimary } from '../../../styles/colors';
import { bs } from '../../../styles/styles';

export const components: Options['components'] = {
	ol: ({ node, children, className, ...props }) => (
		<ol className={css(s.ol)} {...props}>
			{children}
		</ol>
	),
	li: ({ node, children, className, ...props }) => (
		<li className={css(s.li)} {...props}>
			{children}
		</li>
	),
	ul: ({ node, children, className, ...props }) => (
		<ul className={css(s.ul)} {...props}>
			{children}
		</ul>
	),
	hr: ({ node, className, ...props }) => <hr className={css(s.hr)} {...props} />,
	strong: ({ node, children, className, ...props }) => (
		<strong className={css(s.strong)} {...props}>
			{children}
		</strong>
	),
	a: ({ node, children, className, href, ...props }) => (
		<a className={css(s.a)} href={href} rel='noreferrer' target='_blank' {...props}>
			{children}
		</a>
	),
	h1: ({ node, children, className, ...props }) => (
		<h1 className={css(s.h1)} {...props}>
			{children}
		</h1>
	),
	h2: ({ node, children, className, ...props }) => (
		<h2 className={css(s.h2)} {...props}>
			{children}
		</h2>
	),
	h3: ({ node, children, className, ...props }) => (
		<h3 className={css(s.h3)} {...props}>
			{children}
		</h3>
	),
	h4: ({ node, children, className, ...props }) => (
		<h4 className={css(s.h4)} {...props}>
			{children}
		</h4>
	),
	h5: ({ node, children, className, ...props }) => (
		<h5 className={css(s.h5)} {...props}>
			{children}
		</h5>
	),
	h6: ({ node, children, className, ...props }) => (
		<h6 className={css(s.h6)} {...props}>
			{children}
		</h6>
	),
	table: ({ node, children, className, ...props }) => (
		<div className={css(bs.boxBorder, bs.my4, bs.overflowXAuto)}>
			<table className={css(s.table)} {...props}>
				{children}
			</table>
		</div>
	),
	thead: ({ node, children, className, ...props }) => (
		<thead className={css(s.thead)} {...props}>
			{children}
		</thead>
	),
	tbody: ({ node, children, className, ...props }) => (
		<tbody className={css(s.tbody)} {...props}>
			{children}
		</tbody>
	),
	tr: ({ node, children, className, ...props }) => (
		<tr className={css(s.tr)} {...props}>
			{children}
		</tr>
	),
	th: ({ node, children, className, ...props }) => (
		<th className={css(s.th)} {...props}>
			{children}
		</th>
	),
	td: ({ node, children, className, ...props }) => (
		<td className={css(s.td)} {...props}>
			{children}
		</td>
	),
	blockquote: ({ node, children, className, ...props }) => (
		<blockquote className={css(s.blockQuote)} {...props}>
			{children}
		</blockquote>
	),
	code: ({ node, className, ...props }) => {
		const inline = node?.position?.start.line === node?.position?.end.line;

		if (!inline) {
			return <code className={className} {...props} />;
		}

		return <code className={css(s.code)} {...props} />;
	},
	sup: ({ node, children, className, ...props }) => (
		<sup className={css(s.sup)} {...props}>
			{children}
		</sup>
	),
	sub: ({ node, children, className, ...props }) => (
		<sub className={css(s.sub)} {...props}>
			{children}
		</sub>
	),
};

const s = StyleSheet.create({
	ol: {
		boxSizing: 'border-box',
		margin: 0,
		padding: 0,
		marginLeft: 16,
		listStyle: 'decimal !important',
		listStylePosition: 'outside',
	},
	ul: {
		boxSizing: 'border-box',
		margin: 0,
		padding: 0,
		marginLeft: 16,
		listStyle: 'disc !important',
		listStylePosition: 'outside',
	},
	li: {
		boxSizing: 'border-box',
		margin: 0,
		padding: 0,
		paddingTop: 4,
		paddingBottom: 4,
		listStyle: 'inherit !important',
	},
	hr: {
		boxSizing: 'border-box',
		margin: 0,
		padding: 0,
		marginTop: 24,
		marginBottom: 24,
		borderColor,
	},
	a: {
		boxSizing: 'border-box',
		color: brandPrimary,
		textDecoration: 'underline',
	},
	h1: {
		boxSizing: 'border-box',
		margin: 0,
		padding: 0,
		marginTop: 24,
		marginBottom: 8,
		fontWeight: 'bold',
		fontSize: '1.875rem',
		lineHeight: 1.2,
	},
	h2: {
		boxSizing: 'border-box',
		margin: 0,
		padding: 0,
		marginTop: 24,
		marginBottom: 8,
		fontWeight: 'bold',
		fontSize: '1.5rem',
		lineHeight: 1.33,
	},
	h3: {
		boxSizing: 'border-box',
		margin: 0,
		padding: 0,
		marginBottom: 8,
		fontWeight: 'bold',
		fontSize: '1.25rem',
		lineHeight: 1.4,
		':not(:first-child)': {
			marginTop: 24,
		},
	},
	h4: {
		boxSizing: 'border-box',
		margin: 0,
		padding: 0,
		marginTop: 24,
		marginBottom: 8,
		fontWeight: 'bold',
		fontSize: '1.125rem',
		lineHeight: 1.55,
	},
	h5: {
		boxSizing: 'border-box',
		margin: 0,
		padding: 0,
		marginTop: 24,
		marginBottom: 8,
		fontWeight: 'bold',
		fontSize: '1rem',
		lineHeight: 1.5,
	},
	h6: {
		boxSizing: 'border-box',
		margin: 0,
		padding: 0,
		marginTop: 24,
		marginBottom: 8,
		fontWeight: 'bold',
		fontSize: '0.875rem',
		lineHeight: 1.43,
	},
	table: {
		boxSizing: 'border-box',
		margin: 0,
		padding: 0,
		width: '100%',
		borderCollapse: 'collapse',
		border: `1px solid ${borderColor}`,
	},
	thead: {
		boxSizing: 'border-box',
		margin: 0,
		padding: 0,
		backgroundColor: `rgba(38, 38, 38, 0.5)`,
	},
	tbody: {
		boxSizing: 'border-box',
		margin: 0,
		padding: 0,
		borderColor,
		borderStyle: 'solid',
		':has(*) > :not(:last-child)': {
			borderTopWidth: 0,
			borderBottomWidth: 1,
		},
	},
	tr: {
		boxSizing: 'border-box',
		margin: 0,
		padding: 0,
		borderColor,
		borderStyle: 'solid',
		borderBottomWidth: 1,
	},
	th: {
		boxSizing: 'border-box',
		margin: 0,
		paddingLeft: 16,
		paddingRight: 16,
		paddingTop: 8,
		paddingBottom: 8,
		textAlign: 'left',
		fontWeight: 'bold',
		fontSize: '0.875rem',
		lineHeight: 1.43,
	},
	td: {
		boxSizing: 'border-box',
		margin: 0,
		paddingLeft: 16,
		paddingRight: 16,
		paddingTop: 8,
		paddingBottom: 8,
		fontSize: '0.875rem',
		lineHeight: 1.43,
	},
	blockQuote: {
		boxSizing: 'border-box',
		margin: 0,
		padding: 0,
		marginTop: 16,
		marginBottom: 16,
		borderLeft: `4px solid rgba(115, 115, 115, 0.3)`,
		paddingLeft: 16,
		color: `rgb(115 115 115)`,
		fontStyle: 'italic',
	},
	code: {
		boxSizing: 'border-box',
		margin: 0,
		borderRadius: 4,
		backgroundColor: `#262626`,
		color: '#e5e5e5',
		paddingLeft: 6,
		paddingRight: 6,
		paddingTop: 2,
		paddingBottom: 2,
		fontSize: '0.875rem',
		lineHeight: 1.43,
		fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
	},
	sup: {
		boxSizing: 'border-box',
		margin: 0,
		padding: 0,
		fontSize: '0.875rem',
		lineHeight: 1.43,
	},
	sub: {
		boxSizing: 'border-box',
		margin: 0,
		padding: 0,
		fontSize: '0.875rem',
		lineHeight: 1.43,
	},
	strong: {
		boxSizing: 'border-box',
		margin: 0,
		padding: 0,
		fontWeight: 700,
	},
});
